import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import type { PlaneType } from '../../../../game/Constants';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { selectorBadges, selectorIcons, selectorTooltip } from '../../../theme/selector.css';
import { scrollbarThin } from '../../../theme/scrollbar.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy, type CtfPlaneRoster } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { AIRCRAFT, type Aircraft } from '../loadout-data';

/** Stat keys to render (in display order) and their human-readable labels. */
const STAT_LABELS = [
  ['agility', 'Agility'],
  ['defense', 'Defense'],
  ['regen', 'Regen'],
  ['damage', 'Damage'],
] as const;

/**
 * Aircraft Selector - Displays aircraft selection buttons
 *
 * Shows on the left side of the game screen with:
 * - 5 aircraft buttons (Predator, Goliath, Mohawk, Tornado, Prowler)
 * - Highlights currently selected aircraft
 * - Tooltip on hover: stats bars + ability in non-CTF, team rosters in CTF
 * - Per-team count badges on each icon in CTF
 *
 * Desktop only — on mobile, airmash-game omits it from the sidebar and the
 * toolbar loadout-panel popover takes over (clears the touch-control zones).
 *
 * @element aircraft-selector
 */
@customElement('aircraft-selector')
export class AircraftSelector extends LitElement {
  static override styles = [
    tokens,
    scrollbarThin,
    selectorTooltip,
    selectorIcons,
    selectorBadges,
    css`
      :host {
        display: block;
        width: 44px;
        pointer-events: auto;
        /* Declared here rather than inherited from the body — the component
           owns its own typography, and a slow click on a button or its card
           shouldn't select the text. Same set as upgrade- and cruise-selector. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
      }

      .aircraft {
        width: 44px;
        height: 44px;
        background-repeat: no-repeat;
        background-size: 44px 44px;
        background-position: 0 0;
        background-color: var(--color-bg-selector);
        cursor: pointer;
        position: relative;
        /* Harmless on desktop; keeps taps crisp on a touch screen the mobile
           check misses — no double-tap-zoom hesitation or grey tap flash. */
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      .aircraft:first-child {
        border-top-left-radius: var(--radius-md);
        border-top-right-radius: var(--radius-md);
      }

      .aircraft:last-child {
        border-bottom-left-radius: var(--radius-md);
        border-bottom-right-radius: var(--radius-md);
      }

      .aircraft.selected {
        background-color: var(--color-bg-selector-hover);
      }

      /* Hover-only — the sidebar is desktop-only, but a touch-only device the
         mobile check doesn't catch still renders it, and there a tap would
         leave the button lit (and its card open, below) after the finger lifts.
         Same gate as upgrade- and cruise-selector. */
      @media (hover: hover) {
        .aircraft:hover {
          background-color: var(--color-bg-selector-hover);
        }
      }

      /* The per-plane icon (keyed by cssClass on the AIRCRAFT array) and the
         CTF .count-badge come from selectorIcons / selectorBadges, shared with
         loadout-panel. */

      /* Roster section — replaces stats/ability block in CTF tooltips */
      .roster-section {
        margin-top: 8px;
        max-height: 220px;
        overflow-y: auto;
        /* Tooltip container is fixed-width 180px; scrollbar reserve avoids reflow when scrollbar appears */
        scrollbar-gutter: stable;
      }

      .roster-column + .roster-column {
        margin-top: 6px;
      }

      .roster-name {
        font-size: var(--font-size-xs);
        line-height: 1.5;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .roster-name.blue {
        color: var(--color-team-blue);
      }

      .roster-name.red {
        color: var(--color-team-red);
      }

      .roster-empty {
        font-size: var(--font-size-xs);
        color: var(--color-text-tertiary);
        font-style: italic;
      }

      /* Tooltip — the shared shell (container, arrow, header, .item, desc,
         clickto) lives in selectorTooltip. Below are the aircraft-specific
         stat-bar children and the hover trigger, gated like the hover above. */
      @media (hover: hover) {
        .aircraft:hover .tooltip {
          display: block;
        }
      }

      /* Stat items use flex to avoid inline-block whitespace issues */
      .tooltip .stat-item {
        display: flex;
        align-items: center;
      }

      .tooltip .item .name {
        color: var(--color-text-link);
        width: 100px;
      }

      .tooltip .item .val {
        width: 80px;
        height: 16px;
        position: relative;
      }

      .tooltip .item .val .bar {
        position: absolute;
        left: 0;
        top: 0;
        display: block;
        width: 80px;
        height: 16px;
        border-radius: 7px;
        background-color: var(--color-bg-stat-bar);
        overflow: hidden;
        box-shadow: inset 0 3px 0 rgba(0, 0, 0, 0.15);
      }

      .tooltip .item .val .bar .progress {
        display: block;
        height: 16px;
        border-radius: 7px;
        background: var(--gradient-stat-bar);
        box-shadow: inset 0 -3px 0 rgba(0, 0, 0, 0.15);
      }

      .tooltip .item .special {
        display: inline-block;
        font-weight: bold;
        padding: 3px 8px;
        background-color: var(--color-bg-badge);
        margin-top: 14px;
        margin-bottom: 8px;
        color: var(--color-text-white);
        border-radius: var(--radius-md);
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  /** The hover tooltip is informational (cursor: default) but sits inside the
   *  clickable .aircraft button, so a click on the stats card would otherwise
   *  bubble up and select/respawn the plane. Swallow it. Click only — hover
   *  still bubbles, so moving onto the card to read it keeps it open. */
  private stopTooltipClick = (e: Event) => {
    e.stopPropagation();
  };

  override render() {
    const { selectedAircraft, ctfPlaneRosters } = this.uiState;

    return html`
      ${AIRCRAFT.map((aircraft) => {
        const roster = ctfPlaneRosters?.[aircraft.type] ?? null;
        const blueCount = roster?.blue.length ?? 0;
        const redCount = roster?.red.length ?? 0;

        return html`
          <div
            class="aircraft ${aircraft.cssClass} ${selectedAircraft === aircraft.type ? 'selected' : ''}"
            @click=${() => this.handleClick(aircraft.type)}
          >
            ${roster && blueCount > 0 ? html`<span class="count-badge blue">${blueCount}</span>` : nothing}
            ${roster && redCount > 0 ? html`<span class="count-badge red">${redCount}</span>` : nothing}

            <div class="tooltip" @click=${this.stopTooltipClick}>
              <div class="arrow"></div>
              <div class="header">${aircraft.name}</div>
              ${roster ? this.renderRoster(roster) : this.renderStats(aircraft)}
            </div>
          </div>
        `;
      })}
    `;
  }

  /** Renders the stats / ability / respawn-hint block (non-CTF tooltip). */
  private renderStats(aircraft: Aircraft) {
    return html`
      ${STAT_LABELS.map(
        ([key, label]) => html`
          <div class="item stat-item">
            <div class="name">${label}</div>
            <div class="val">
              <div class="bar">
                <div class="progress" style="width: ${aircraft.stats[key]}%"></div>
              </div>
            </div>
          </div>
        `,
      )}
      <div class="item">
        <div class="special">${aircraft.ability}</div>
        <div class="desc">${aircraft.abilityDesc}</div>
      </div>
      <div class="item">
        <div class="clickto">Click or press ${aircraft.type} in spectate mode to respawn</div>
      </div>
    `;
  }

  /** Renders the per-team roster section in the tooltip (CTF only). */
  private renderRoster(roster: CtfPlaneRoster) {
    const { blue, red } = roster;
    if (blue.length === 0 && red.length === 0) {
      return html`
        <div class="roster-section scrollbar-thin">
          <div class="roster-empty">No players using this plane</div>
        </div>
      `;
    }
    return html`
      <div class="roster-section scrollbar-thin">
        ${
          blue.length > 0
            ? html`<div class="roster-column">
                ${blue.map((p) => html`<div class="roster-name blue">${p.name}</div>`)}
              </div>`
            : nothing
        }
        ${
          red.length > 0
            ? html`<div class="roster-column">
                ${red.map((p) => html`<div class="roster-name red">${p.name}</div>`)}
              </div>`
            : nothing
        }
      </div>
    `;
  }

  /** Handle aircraft button click */
  private handleClick(type: PlaneType): void {
    this.gameApi.playClick();
    this.gameApi.selectAircraft(type);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'aircraft-selector': AircraftSelector;
  }
}
